import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import StepShell from '@/components/StepShell';
import Scale from '@/components/Scale';
import { useSession } from '@/store/session';
import type { MouilletteScores } from '@/types';

const MOUILLETTES: Array<{
  id: keyof MouilletteScores;
  code: string;
  label: string;
  desc: string;
  hint: string;
  gradient: string;
}> = [
  {
    id: 'citrico',
    code: 'M1',
    label: 'Cítrico / fresco',
    desc: 'Bergamota, limón, hierba',
    hint: 'Despierta y refresca',
    gradient: 'linear-gradient(135deg, #E9C46A 0%, #A8C77A 100%)',
  },
  {
    id: 'floral',
    code: 'M2',
    label: 'Floral',
    desc: 'Rosa, jazmín, azahar',
    hint: 'Romántico y luminoso',
    gradient: 'linear-gradient(135deg, #E8B4D9 0%, #C9A961 100%)',
  },
  {
    id: 'amaderado',
    code: 'M3',
    label: 'Amaderado',
    desc: 'Sándalo, cedro, vetiver',
    hint: 'Cálido, equilibrado',
    gradient: 'linear-gradient(135deg, #8B6F47 0%, #4A3520 100%)',
  },
  {
    id: 'oriental',
    code: 'M4',
    label: 'Oriental / ámbar',
    desc: 'Vainilla, ámbar, especias',
    hint: 'Envolvente y sensual',
    gradient: 'linear-gradient(135deg, #B8552B 0%, #6B3410 100%)',
  },
  {
    id: 'oud',
    code: 'M5',
    label: 'Oud / especiado',
    desc: 'Oud, azafrán, cuero',
    hint: 'Profundo y misterioso',
    gradient: 'linear-gradient(135deg, #3A1F0F 0%, #0B0B0B 100%)',
  },
];

export default function Mouillettes() {
  const navigate = useNavigate();
  const { mouillettes, setMouillettes } = useSession();

  const isValid = useMemo(
    () => MOUILLETTES.every((m) => mouillettes[m.id] != null),
    [mouillettes]
  );

  return (
    <StepShell
      step={6}
      title="Prueba olfativa"
      subtitle="El vendedor te presenta 5 mouillettes sin marca. Huele cada una y puntúa según cuánto te gusta."
      onBack={() => navigate('/mi-perfume')}
      onNext={() => navigate('/resultado')}
      nextDisabled={!isValid}
    >
      {/* Mini explicación para clientes y vendedores que no conozcan el término */}
      <details className="card mb-6 mx-auto max-w-2xl">
        <summary className="cursor-pointer text-elixir-gold-soft text-sm tracking-[0.18em] uppercase select-none flex items-center justify-between">
          <span>¿Qué es una mouillette?</span>
          <span className="text-xs text-elixir-cream/45 normal-case tracking-normal">tocar para abrir</span>
        </summary>
        <div className="mt-4 text-sm text-elixir-cream/75 leading-relaxed serif">
          <p>
            Una <em>mouillette</em> es la <strong>tira de papel</strong> que los perfumistas
            profesionales usan para oler una fragancia sin ponérsela en la piel. Permite probar
            varios perfumes seguidos sin saturarse y, sobre todo, evaluar el aroma de forma honesta
            sin que la marca o el frasco influyan en la opinión.
          </p>
          <p className="mt-3">
            En esta prueba el vendedor te presenta <strong>5 mouillettes ciegas</strong> — 5 tiras
            sin etiqueta —, cada una representando una familia olfativa distinta:
            <em> cítrico, floral, amaderado, oriental y oud</em>. Tu trabajo: oler cada una y puntuar
            de 1 (no me dice nada) a 5 (me encanta) según lo que sientas. Sin pensarlo demasiado.
          </p>
        </div>
      </details>

      {/* Contador de progreso de mouillettes votadas */}
      {(() => {
        const voted = MOUILLETTES.filter((m) => mouillettes[m.id] != null).length;
        const total = MOUILLETTES.length;
        return (
          <div className="mb-5 max-w-2xl mx-auto">
            <div className="flex items-center justify-between text-xs text-elixir-cream/55 mb-1.5">
              <span>Progreso de la prueba</span>
              <span className={voted === total ? 'text-elixir-gold font-semibold' : ''}>
                {voted} / {total} valoradas
              </span>
            </div>
            <div className="h-1 bg-elixir-cream/10 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-500 rounded-full ${
                  voted === total ? 'bg-elixir-emerald' : 'bg-gradient-to-r from-elixir-gold to-elixir-gold-soft'
                }`}
                style={{ width: (voted / total) * 100 + '%' }}
              />
            </div>
          </div>
        );
      })()}

      <div className="space-y-5">
        {MOUILLETTES.map((m, idx) => {
          const voted = mouillettes[m.id] != null;
          return (
            <section
              key={m.id}
              className={`card transition-all duration-500 ${
                voted ? 'ring-1 ring-elixir-emerald/50 shadow-card' : 'hover:shadow-card-hover'
              }`}
              style={{ animationDelay: `${idx * 80}ms` }}
            >
              <div className="flex items-start gap-4 mb-4">
                {/* Tarjeta visual de la mouillette */}
                <div
                  className="relative flex-shrink-0 w-20 h-28 rounded-xl2 flex flex-col items-center justify-center text-white shadow-card"
                  style={{ background: m.gradient }}
                >
                  <span className="display text-2xl">{m.code}</span>
                  <span className="text-[10px] opacity-80 mt-1">mouillette</span>
                  {voted && (
                    <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-elixir-emerald text-elixir-cream flex items-center justify-center text-sm shadow-card animate-fade-up">
                      ✓
                    </span>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="display text-2xl text-elixir-cream leading-tight">{m.label}</h3>
                  <p className="text-sm text-elixir-cream/60 mt-1">{m.desc}</p>
                  <p className="text-xs text-elixir-gold/80 italic mt-1">{m.hint}</p>
                </div>
              </div>

              <Scale
                steps={5}
                leftLabel="No me dice nada"
                rightLabel="Me encanta"
                value={mouillettes[m.id]}
                onChange={(v) => setMouillettes({ [m.id]: v as 1 | 2 | 3 | 4 | 5 })}
              />
            </section>
          );
        })}
      </div>

      {!isValid && (
        <p className="mt-6 text-xs text-elixir-cream/45 text-center">
          Puntúa las 5 mouillettes para ver tu resultado.
        </p>
      )}
      {isValid && (
        <p className="mt-6 text-sm text-elixir-emerald text-center animate-fade-up">
          ✓ Todas las mouillettes valoradas — pulsa Continuar para ver tu resultado
        </p>
      )}
    </StepShell>
  );
}
